import { CSSProperties, Dispatch, SetStateAction } from "react";
import { InputChangeEventDetail, IonInput } from "@ionic/react";
import { DateTime } from "luxon";
import { SchoolClosure, SchoolModel } from "model";
import { Asterisk, Colors, DateInput, DisabledDateRanges } from "swing-components";
import { translate } from "utils/string-table";

import { IconButton } from "~components/Molecules/IconButton";

import { calcHours } from "../SchoolYears/utils";
import { ClosureMap } from "./SchoolClosuresView";
import { getMaxEndDate, getMaxStartDate } from "./utils";

const styles = {
  button: {
    fontSize: 30,
    marginRight: 16,
    border: "none",
    color: Colors.red500,
    background: "transparent",
  },
  dateFieldContainer: {
    display: "inline-block",
    marginRight: 8,
  },
  inputContainer: {
    display: "block",
    borderBottom: `1px solid ${Colors.slate400}`,
  },
} satisfies Record<string, CSSProperties>;

export type ClosureField = SchoolClosure & { isRemoved?: boolean };

type ClosureFieldRowProps = {
  id: string;
  school: SchoolModel;
  closureField: ClosureField;
  closures: SchoolClosure[];
  setClosure: Dispatch<SetStateAction<ClosureMap>>;
};

/**
 *
 * @param props closure = represents the closure field itself
 * @param props closures = used to make sure we don't allow overlapping dates
 * @returns
 */
export function ClosureFieldRow(props: ClosureFieldRowProps) {
  const {
    id,
    closureField: { id: closureId, start, end, reason, isRemoved },
    school: { hours },
    closures,
    setClosure,
  } = props;

  /*** Constants ***/
  // Convert the start and end dates to DateTime objects to make sure they are
  // brought back into the School's timezone.
  const startDateTime = DateTime.fromISO(start);
  const endDateTime = DateTime.fromISO(end);

  /*** Disabled Date Ranges ***/
  // Disabled dates for all DateInputs which are not the current closure
  const otherClosuresDisabledDates: DisabledDateRanges = closures
    .filter(
      // Extra filtering is being applied for closures that are not fully
      // defined.
      (closure) => closure.start && closure.start !== start && closure.end && closure.end !== end,
    )
    .map((closure) => {
      const closureStartDateTime = DateTime.fromISO(closure.start);
      const closureEndDateTime = DateTime.fromISO(closure.end);

      return {
        start: closureStartDateTime.toISODate(),
        // NOTE: Since end is exclusive, we must add 1 day to the end of the
        // disabled range.
        end: closureEndDateTime.plus({ day: 1 }).toISODate(),
        label: translate("SCHOOL_CLOSURES_NON_SCHOOL_DAYS_OTHER_DISABLED_LABEL"),
      };
    });

  // Disable dates for the Start DateInput
  // @ts-expect-error - Specific type narrowing is not working as expected.
  const startDateInputDisabledDateRanges: DisabledDateRanges = [
    // Disabled the ability to select an overlapping closure if there are
    // other closures and the end date is selected
    ...(otherClosuresDisabledDates.length > 0 && end
      ? [
          {
            end: getMaxStartDate({ closures, end }),
            label: translate("SCHOOL_CLOSURES_NON_SCHOOL_DAYS_OVERLAPPING_DISABLED_LABEL"),
          },
        ]
      : []),
    ...otherClosuresDisabledDates,
    // If there is an end date for this closure, then disable selecting past the end date.
    ...(end
      ? [
          {
            // NOTE: We must add 1 day to the start of this disabled range due
            // to the date being able to be selected when the end date is also
            // this date.
            start: endDateTime.plus({ day: 1 }).toISODate(),
            label: translate("SCHOOL_CLOSURES_NON_SCHOOL_DAYS_START_DISABLED_LABEL", {
              endDate: DateTime.fromISO(end),
            }),
          },
        ]
      : []),
  ];

  // Disabled dates for the End DateInput
  // @ts-expect-error - Specific type narrowing is not working as expected.
  const endDateInputDisabledDateRanges: DisabledDateRanges = [
    // If there is a start date for this closure, then disable selecting before the start date.
    ...(start
      ? [
          {
            end: startDateTime.toISODate(),
            label: translate("SCHOOL_CLOSURES_NON_SCHOOL_DAYS_END_DISABLED_LABEL", {
              startDate: DateTime.fromISO(start),
            }),
          },
        ]
      : []),
    // Disable dates for all other closures
    ...otherClosuresDisabledDates,
    // Disabled the ability to select an overlapping closure if there are
    // other closures and the start date is selected
    ...(otherClosuresDisabledDates.length > 0 && start
      ? [
          {
            start: getMaxEndDate(closures, start),
            label: translate("SCHOOL_CLOSURES_NON_SCHOOL_DAYS_OVERLAPPING_DISABLED_LABEL"),
          },
        ]
      : []),
  ];

  return (
    <li css={{ display: "flex", padding: 0, paddingBottom: 0, marginBottom: 16 }}>
      <div css={{ alignSelf: "start" }}>
        {/* Delete / restore date(s) button */}
        <IconButton
          aria-label={translate(
            isRemoved ? "ARIA_SCHOOL_CLOSURES_RESTORE_DAYS" : "ARIA_SCHOOL_CLOSURES_REMOVE_DAYS",
          )}
          iconProps={{
            name: isRemoved ? "Refresh" : "Close",
            color: isRemoved ? Colors.slate600 : Colors.red500,
          }}
          css={styles.button}
          onClick={() =>
            setClosure((prevState) => {
              const newMap = new Map(prevState);
              const obj = newMap.get(id);

              if (!obj) return newMap;

              if (closureId) {
                return newMap.set(id, { ...obj, isRemoved: !isRemoved });
              } else {
                newMap.delete(id);
                return newMap;
              }
            })
          }
        />
      </div>
      <div css={{ width: "100%" }}>
        <div css={styles.dateFieldContainer}>
          {/* Start DateInput */}
          <DateInput
            value={startDateTime.toISODate()}
            isStrikethrough={isRemoved}
            disabledDateRanges={startDateInputDisabledDateRanges}
            onChange={(newStartDate) => {
              const newStartDateTime = calcHours({
                hours,
                time: DateTime.fromISO(newStartDate),
                type: "start",
              });

              setClosure((prevState) => {
                const newMap = new Map(prevState);
                const obj = newMap.get(id);

                if (!obj) return newMap;

                return newMap.set(id, {
                  ...obj,
                  start: newStartDateTime,
                });
              });
            }}
          />
        </div>
        -{/* End DateInput */}
        <div
          css={{
            ...styles.dateFieldContainer,
            marginLeft: 8,
          }}
        >
          <DateInput
            value={endDateTime.toISODate()}
            isStrikethrough={isRemoved}
            disabledDateRanges={endDateInputDisabledDateRanges}
            onChange={(newEndDate) => {
              const newEndDateTime = calcHours({
                hours,
                time: DateTime.fromISO(newEndDate),
                type: "end",
              });

              setClosure((prevState) => {
                const newMap = new Map(prevState);
                const obj = newMap.get(id);

                if (!obj) return newMap;

                return newMap.set(id, {
                  ...obj,
                  end: newEndDateTime,
                });
              });
            }}
          />
        </div>
        <div css={styles.inputContainer}>
          <IonInput
            value={reason}
            placeholder={translate("SCHOOL_CLOSURES_REASON_PLACEHOLDER")}
            disabled={isRemoved}
            maxlength={90}
            labelPlacement="floating"
            onIonInput={(e: CustomEvent<InputChangeEventDetail>) =>
              setClosure((prevState) => {
                const newMap = new Map(prevState);
                const obj = newMap.get(id);

                if (!obj) return newMap;

                return newMap.set(id, {
                  ...obj,
                  reason: e.detail.value || "",
                });
              })
            }
          >
            <div slot="label">
              {translate("SCHOOL_CLOSURES_LABEL")}{" "}
              <span css={{ color: Colors.red500, fontWeight: 600 }}>
                <Asterisk />
              </span>
            </div>
          </IonInput>
        </div>
      </div>
    </li>
  );
}
